import React, { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { X, Heart, MessageCircle, Calendar, Send } from "lucide-react";

export default function PostDetailModal({ post, onClose }) {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [liked, setLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(post.likes_count);
  const [commentsCount, setCommentsCount] = useState(post.comments_count);
  const [submittingComment, setSubmittingComment] = useState(false);

  const loadComments = useCallback(async () => {
    try {
      const res = await api.get(`/api/posts-updates/${post.id}/comments?limit=50`);
      setComments(res.data.comments);
    } catch (e) {
      console.error("Failed to load comments:", e);
    }
  }, [post.id]);

  const checkIfLiked = useCallback(async () => {
    try {
      const res = await api.get(`/api/posts-updates/${post.id}/is-liked`);
      setLiked(res.data.liked);
    } catch (e) {
      // Not logged in or error
      setLiked(false);
    }
  }, [post.id]);

  useEffect(() => {
    loadComments();
    checkIfLiked();
  }, [post.id, loadComments, checkIfLiked]);

  const handleLike = async () => {
    try {
      const res = await api.post(`/api/posts-updates/${post.id}/like`);
      setLiked(res.data.liked);
      setLikesCount(res.data.likes_count);
    } catch (e) {
      if (e.response?.status === 401) {
        toast.error("Please login to like posts");
      } else {
        toast.error("Failed to like post");
      }
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    
    if (!newComment.trim()) return;
    if (newComment.length > 500) {
      toast.error("Comment too long (max 500 characters)");
      return;
    }

    setSubmittingComment(true);
    try {
      const res = await api.post(`/api/posts-updates/${post.id}/comment`, {
        content: newComment.trim()
      });
      
      setComments([res.data.comment, ...comments]);
      setCommentsCount(commentsCount + 1);
      setNewComment("");
      toast.success("Comment posted!");
    } catch (e) {
      if (e.response?.status === 401) {
        toast.error("Please login to comment");
      } else {
        toast.error(e?.response?.data?.detail || "Failed to post comment");
      }
    } finally {
      setSubmittingComment(false);
    }
  };

  const getTimeSince = (dateString) => {
    const date = new Date(dateString);
    const seconds = Math.floor((new Date() - date) / 1000);
    
    if (seconds < 60) return "just now";
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fadeIn">
      <div
        className="relative w-full max-w-3xl max-h-[90vh] bg-[#0F1118] rounded-2xl border border-white/20 shadow-2xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <h2 className="text-xl font-bold pr-8" style={{ fontFamily: "'Syne', sans-serif" }}>
            {post.title}
          </h2>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Featured Image */}
          {post.featured_image && (
            <div className="relative h-64 overflow-hidden">
              <img
                src={post.featured_image}
                alt={post.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* Post Content */}
          <div className="p-6">
            {/* Meta */}
            <div className="flex items-center gap-4 mb-4 text-sm text-zinc-400">
              <span className="px-3 py-1 bg-gradient-to-r from-[#E11D48] to-[#FF10F0] text-white rounded-full text-xs font-bold">
                {post.category}
              </span>
              <span className="flex items-center gap-1">
                <Calendar size={14} />
                {new Date(post.created_at).toLocaleDateString()}
              </span>
            </div>

            {/* Rich Content */}
            <div
              className="prose prose-invert max-w-none mb-6"
              dangerouslySetInnerHTML={{ __html: post.content }}
              style={{
                color: "var(--gio-text)",
              }}
            />

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {post.tags.map((tag, i) => (
                  <span
                    key={i}
                    className="px-2 py-1 bg-white/5 rounded text-xs text-zinc-400"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            )}

            {/* Engagement */}
            <div className="flex items-center gap-4 py-4 border-t border-b border-white/10 mb-6">
              <button
                onClick={handleLike}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all ${
                  liked
                    ? "bg-[#E11D48] text-white"
                    : "bg-white/5 text-zinc-400 hover:bg-white/10 hover:text-white"
                }`}
              >
                <Heart size={18} fill={liked ? "currentColor" : "none"} />
                {likesCount}
              </button>
              <div className="flex items-center gap-2 text-zinc-400">
                <MessageCircle size={18} />
                {commentsCount} comments
              </div>
            </div>

            {/* Comments Section */}
            <div>
              <h3 className="font-bold text-lg mb-4">Comments</h3>

              {/* Comment Form */}
              <form onSubmit={handleSubmitComment} className="mb-6">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a comment..."
                    maxLength={500}
                    className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-[#E11D48] transition-colors"
                    disabled={submittingComment}
                  />
                  <button
                    type="submit"
                    disabled={!newComment.trim() || submittingComment}
                    className="px-4 py-2 bg-gradient-to-r from-[#E11D48] to-[#FF10F0] text-white rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-opacity flex items-center gap-2"
                  >
                    <Send size={16} />
                  </button>
                </div>
                <div className="text-xs text-zinc-500 mt-1 text-right">
                  {newComment.length}/500
                </div>
              </form>

              {/* Comments List */}
              <div className="space-y-4">
                {comments.length === 0 ? (
                  <p className="text-center text-zinc-500 py-8">No comments yet. Be the first!</p>
                ) : (
                  comments.map((comment) => (
                    <div key={comment.id} className="flex gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#E11D48] to-[#FF10F0] flex items-center justify-center text-xs font-bold flex-shrink-0">
                        {comment.member_name.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-sm">{comment.member_name}</span>
                          <span className="text-xs text-zinc-500">{getTimeSince(comment.created_at)}</span>
                        </div>
                        <p className="text-sm text-zinc-300">{comment.content}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
